import { ComposerPrimitive, useAui, useAuiState } from '@assistant-ui/react';
import { motion } from 'motion/react';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './Composer.module.css';
import { ComposerAction } from './ComposerAction.tsx';
import { useHasPendingToolCall } from '#plugin-chat/hooks/useHasPendingToolCall.ts';

/** Chat message composer with auto-resizing textarea and send/cancel actions. */
export function Composer() {
  const { t } = useTranslation('chat');
  const aui = useAui();
  const hasPendingToolCall = useHasPendingToolCall();
  const composerIsEmpty = useAuiState((s) => s.composer.isEmpty);
  // Create motion-enabled version of ComposerPrimitive.Root to support CSS transitions on mount/unmount
  const Root = motion.create(ComposerPrimitive.Root);

  // assistant-ui's `ComposerPrimitive.Input` blocks Enter submission while
  // `thread.isRunning` (and no queue capability) and *returns without*
  // *`preventDefault`*, so the browser inserts a newline. During a
  // `requires-action` interrupt (e.g. `askUserQuestion` awaiting its
  // `human()` callback) the AI SDK run stays "running", so a user typing a
  // new message just accumulates newlines and Send is disabled. Intercept
  // Enter here: assistant-ui composes this `onKeyDown` *before* its own
  // handler and skips that handler when we call `preventDefault`, so this
  // cleanly overrides the newline behaviour via public APIs only — no
  // primitive fork, no global queue-capability change. We trigger the
  // ungated runtime send (`aui.composer().send()`); `wrappedChat.sendMessage`
  // is the single place that cancels the pending interrupt before starting
  // the new turn. Only act while an interrupt is pending and there is text —
  // otherwise defer entirely to assistant-ui's default behaviour.
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!hasPendingToolCall || e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing || composerIsEmpty) {
      return;
    }
    e.preventDefault();
    aui.composer().send();
  };

  return (
    <Root className={classes.composer}>
      <div className={classes.inputContainer}>
        <ComposerPrimitive.Input
          aria-label={t('messageInput')}
          autoFocus
          className={classes.inputTextarea}
          maxRows={5}
          onKeyDown={handleKeyDown}
          placeholder={t('sendMessage')}
          rows={1}
        />
        <ComposerAction />
      </div>
    </Root>
  );
}
